// src/components/messages/MessageInput.tsx
import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { FileAttachment } from '../common/FileAttachment';
import { FileUploadProgress } from '../common/FileUploadProgress';
import FileService, { UploadProgress } from '../../services/FileService';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';

interface MessageInputProps {
  onSend: (content: string, attachments?: any[]) => Promise<void>;
  disabled?: boolean;
  onAttachmentClick?: () => void;
}

export default function MessageInput({ onSend, disabled, onAttachmentClick }: MessageInputProps) {
  const { user } = useAuth();
  const { selectedSchool } = useSchool();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);

  // Scroll when attachments change
  React.useEffect(() => {
    console.log('MessageInput: Attachments changed:', attachments.length);
    if (attachments.length > 0) {
      // Give DOM time to update with the file preview
      setTimeout(() => {
        onAttachmentClick?.();
      }, 100);
    }
  }, [attachments.length, onAttachmentClick]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!message.trim() && attachments.length === 0) || uploading) return;

    console.log('MessageInput: Sending message with attachments:', attachments.length);
    
    try {
      setUploading(true);

      // Send message with attachments (let MessagesPage handle the uploading)
      await onSend(message, attachments);
      
      console.log('MessageInput: Message sent successfully, clearing form');
      
      // Clear form only after successful send
      setMessage('');
      setAttachments([]);
      setUploadProgress([]);
      
      // Trigger scroll after message is sent
      setTimeout(() => {
        onAttachmentClick?.();
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      // Don't clear form on error so user can retry
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 border-t border-border-light space-y-3">
      {/* File Attachments */}
      {attachments.length > 0 && (
        <FileAttachment
          files={attachments}
          onFilesChange={setAttachments}
          maxFiles={5}
          category="message"
          disabled={disabled || uploading}
          showPreview={true}
        />
      )}

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <FileUploadProgress uploads={uploadProgress} />
      )}
      
      {/* Message Input */}
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as any);
              }
            }}
            placeholder="Type a message..."
            disabled={disabled || uploading}
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500
                     focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
          />
        </div>
        
        {/* Attachment Button */}
        
        <button
          type="button"
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt';
            input.onchange = (e) => {
              const files = (e.target as HTMLInputElement).files;
              if (files) {
                setAttachments(prev => [...prev, ...Array.from(files)]);
              }
            };
            input.click();
          }}
          disabled={disabled || uploading}
          className="p-2 text-text-secondary hover:text-primary-500 transition-colors 
                   disabled:opacity-50 disabled:cursor-not-allowed"
          title="Attach files"
        >
          📎
        </button>
        
        <button
          type="submit"
          disabled={disabled || uploading || (!message.trim() && attachments.length === 0)}
          className="p-2 text-primary-500 hover:text-primary-600 transition-colors 
                   disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>

      {uploading && (
        <div className="text-sm text-text-secondary">
          Uploading files and sending message...
        </div>
      )}
    </div>
  );
}
